import { Grid, Stack, Typography } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import ClearIcon from "@mui/icons-material/Clear";
import { black, blue, divider } from "@/theme/colors";
import { useFormContext, useWatch } from "react-hook-form";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useEffect, useMemo, useState } from "react";
import { useEnum } from "@/api/services/global/enum";
import { Select } from "@/components/elements/Select";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";

export const TalentFormLanguageSkillsFields = () => {
  const { control, setValue } = useFormContext<TTalentForm>();
  const { data: foreignLanguageOptions } = useEnum("EForeignLanguage");
  const MAX_OPTION = foreignLanguageOptions?.length;
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const languageSkills = useWatch({ control, name: "languageSkills" }) || [{ language: null, content: null }];

  const hasEmptyField = useMemo(
    () => languageSkills.some((value) => value === null || value === undefined || value.language === null || value.language === undefined),
    [languageSkills],
  );

  const handleAddField = () => {
    if (languageSkills.length < MAX_OPTION && !hasEmptyField) {
      setValue("languageSkills", [...languageSkills, { language: null, content: null }]);
    }
  };

  const handleRemoveField = (index: number) => {
    if (languageSkills.length > 1) {
      const value = languageSkills[index];
      if (!value || value.language === null || value.language === undefined) {
        const updated = languageSkills.filter((_, i) => i !== index);
        setValue("languageSkills", updated);
      } else {
        setDeleteIndex(index);
        setIsDeleteModalOpen(true);
      }
    }
  };

  const confirmDelete = () => {
    if (deleteIndex !== null && languageSkills.length > 1) {
      const updated = languageSkills.filter((_, i) => i !== deleteIndex);
      setValue("languageSkills", updated);
    }
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  useEffect(() => {
    if (!languageSkills || languageSkills.length === 0) {
      setValue("languageSkills", [{ language: null, content: null }]);
    }
  }, [languageSkills, setValue]);

  const getFilteredOptions = (index: number, currentValue: string | number | null | undefined) => {
    if (!foreignLanguageOptions) return [];

    const selectedValues = languageSkills
      .map((value, i) => (i !== index && value?.language != null ? value.language.toString() : null))
      .filter((value): value is string => value !== null);

    return foreignLanguageOptions.filter((option) => {
      const optionValue = option.value.toString();
      if (currentValue != null && optionValue === currentValue.toString()) {
        return true;
      }
      return !selectedValues.includes(optionValue);
    });
  };

  return (
    <>
      <Stack mt={2}>
        {languageSkills.map((value, index) => {
          const filteredOptions = getFilteredOptions(index, value?.language);

          return (
            <Stack key={index} sx={{ mb: languageSkills.length - 1 === index ? 0 : 2 }}>
              <InnerBox p={2} showDivider={false} borderColor={divider.high} bordered={true}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormField control={control} name={`languageSkills.${index}.language`} label="語学（日本語以外の言語）">
                      <Select placeholder="選択してください" options={filteredOptions} />
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField control={control} name={`languageSkills.${index}.content`} label="語学（日本語以外の言語）の内容">
                      <Input placeholder="入力してください" />
                    </FormField>
                  </Grid>
                </Grid>
              </InnerBox>

              {languageSkills.length > 1 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ cursor: "pointer", mt: 1, ml: 0 }}
                  onClick={() => handleRemoveField(index)}
                >
                  <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
                  <Typography variant="cap12Light">削除</Typography>
                </Stack>
              )}
            </Stack>
          );
        })}

        {languageSkills.length < MAX_OPTION && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            mt={2}
            sx={{
              cursor: hasEmptyField ? "not-allowed" : "pointer",
              opacity: hasEmptyField ? 0.5 : 1,
            }}
            onClick={handleAddField}
          >
            <AddCircleOutlineIcon
              sx={{
                color: hasEmptyField ? black[60] : blue[80],
                fontSize: 18,
              }}
            />
            <Typography variant="cap12">語学を追加</Typography>
          </Stack>
        )}
      </Stack>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIndex(null);
        }}
        onOk={confirmDelete}
        message="「語学」を削除してもよろしいですか？"
      />
    </>
  );
};
